import React, { ReactNode } from 'react'
import { useRoleGuard } from '../../../hooks/useRoleGaurd'
import { useAppSelector } from '../../../store/hooks'
import ValidationBanner from '../ValidationBanner'

interface RoleProtectedRouteProps {
  allowedRoles: string[]
  children: ReactNode
}

/**
 * Route guard component that restricts page access based on user roles.
 * Displays System Error banner when profile fetch fails, Access Denied banner when role is unauthorized,
 * or renders children when access is granted.
 * Keeps authorized children mounted during refreshes when a profile already exists.
 *
 * @param allowedRoles - List of role keys permitted to view the child components.
 * @param children - The protected route content.
 * @returns React element representing loading state, error banner, or authorized content.
 */
const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  allowedRoles,
  children,
}) => {
  const { hasAccess, loading, initialized, error } = useRoleGuard(allowedRoles)
  const hasProfile = useAppSelector((state) => Boolean(state.profile.data))

  if ((loading || !initialized) && !hasProfile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-200 p-4">
        <p className="text-lg font-medium text-gray-700">Checking access...</p>
      </div>
    )
  }

  // System error state - profile API call failed (e.g. 404 or server error)
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-200 p-4">
        <div className="w-full max-w-5xl">
          <ValidationBanner
            variant="error"
            className="!rounded-[8px] !border !border-red-600 !bg-white"
            heading={
              <p className="p3 -medium m-0! leading-[1.4]! desktop:whitespace-nowrap">
                System error: Log out of TORT completely, then sign back in.
              </p>
            }
            message={
              <p
                className="p3 -book m-0!"
                style={{
                  color: 'var(--Inky, var(--Color-Inky, #0D1C3D))',
                  fontSize: 'var(--Desktop-P3-Book-font-size, 16px)',
                  fontStyle: 'normal',
                  fontWeight: 325,
                  lineHeight: 'var(--Desktop-P3-Book-line-height, 24px)',
                  letterSpacing: 'var(--Desktop-P3-Book-letter-spacing, 0)',
                }}
              >
                The system is experiencing intermittent issues. If the problem
                continues after re-logging in, email the National TORT Support
                team at{' '}
                <a
                  href="mailto:NAT-TORT@kp.org"
                  className="text-[#0074AD] hover:text-[#0074AD]! transition-none!"
                >
                  NAT-TORT@kp.org
                </a>{' '}
                with your name, employee ID, NUID, computer screenshots, and a
                description of the issue.
              </p>
            }
            compact={false}
          />
        </div>
      </div>
    )
  }

  // Unauthorized state - employee found but does not have permission
  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-200 p-4">
        <div className="w-full max-w-xl">
          <ValidationBanner
            variant="error"
            heading="Access Denied"
            message="You do not have permission to access this page."
          />
        </div>
      </div>
    )
  }

  // Authorized - render children
  return <>{children}</>
}

export default RoleProtectedRoute
